import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FeatureFlagService } from '../services/feature-flag.service';
import { PlatformWideFlagCodes } from '../constants/feature-flags';

@Injectable({ providedIn: 'root' })
export class FeatureFlagStore {
  private readonly featureFlagService = inject(FeatureFlagService);

  /** What this venue was granted. */
  private readonly enabledCodes = signal<string[]>([]);
  /** What the platform has switched on for everyone. */
  private readonly platformCodes = signal<string[]>([]);
  readonly loaded = signal(false);

  private loadPromise: Promise<void> | null = null;

  /** Idempotent: the app initializer and any route guard share a single fetch. */
  load(): Promise<void> {
    return (this.loadPromise ??= this.doLoad());
  }

  private async doLoad(): Promise<void> {
    try {
      // One round trip each, in parallel: the two lists answer different questions and
      // neither can be derived from the other.
      const [codes, platform] = await Promise.all([
        firstValueFrom(this.featureFlagService.getEnabledFlags()),
        firstValueFrom(this.featureFlagService.getPlatformFlags()),
      ]);
      this.enabledCodes.set(codes);
      this.platformCodes.set(platform);
    } catch (err) {
      // Never cache a failure: an admin without a complex yet gets a 400 here, and a
      // transient 5xx must not lock the tab into "no flags" until a reload. Clearing
      // the promise lets the next guard or component retry.
      this.loadPromise = null;
      throw err;
    } finally {
      this.loaded.set(true);
    }
  }

  /**
   * Fetches again, ignoring the memoised result.
   *
   * `load()` deliberately runs once per session, which is right for gating navigation but
   * wrong for a screen whose whole surface is a flag: a super-admin can revoke selling while
   * a manager tab sits open, and that tab would go on offering prices nobody could be charged
   * until someone happened to reload. Pages that are *about* a flag call this on init.
   */
  refresh(): Promise<void> {
    this.loadPromise = null;
    return this.load();
  }

  /** Flags are per-complex, so a new sign-in must not inherit the previous admin's. */
  reset(): void {
    this.enabledCodes.set([]);
    this.platformCodes.set([]);
    this.loaded.set(false);
    this.loadPromise = null;
  }

  /**
   * Each code is answered by exactly one list — never both. A platform-wide code has no
   * per-complex row, so looking for it in the venue's list would always say no; and a
   * per-venue code must never fall back to the platform list, or a venue that was granted
   * nothing would inherit every switch the platform has on.
   */
  isEnabled(code: string): boolean {
    return PlatformWideFlagCodes.has(code)
      ? this.platformCodes().includes(code)
      : this.enabledCodes().includes(code);
  }
}
